import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Megaphone, FileText, BarChart3, Trophy, Sparkles, GraduationCap, Users,
  ChevronRight, Download,
} from 'lucide-react';
import { usePersonaTailoring } from './PersonaTailoring';
import { useChannel } from '../contexts/ChannelContext';
import { resolveImageUrl } from './ImageUpload';
// iter151 · Ads disabled site-wide. AdSlot import removed; the component
// still exists (as a global no-op) for admin previews only.
import { openApplyModal } from './ApplyToBeFeaturedModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PersonaActionPanel — the persona-specific "action surface" that sits near
 * the top of every channel homepage. Each persona gets their own version:
 *
 *   • fan       → the standard sponsored ad slot ("ads on the site")
 *   • brand     → CTAs to PLACE an ad / submit a campaign
 *   • investor  → mission reports & strategic direction documents
 *   • athlete   → brand deal opportunities + "build a campaign" CTAs
 *   • school    → athletes available to sponsor + AD resources
 *
 * Renders nothing if there's no persona, if the user opted out of tailoring,
 * or if the persona has no panel defined.
 */
const PersonaActionPanel = () => {
  const { personaId, personaMeta, isTailoring } = usePersonaTailoring();

  if (!isTailoring || !personaId || !personaMeta) return null;

  switch (personaId) {
    case 'fan':     return <FanPanel />;
    case 'brand':   return <BrandPanel />;
    case 'investor': return <InvestorPanel />;
    case 'athlete': return <AthletePanel />;
    case 'school':  return <SchoolPanel />;
    default: return null;
  }
};

/* ---------- FAN ---------- */
/**
 * Fans see live sponsored content — the platform's revenue side. Pulls
 * approved ads from /api/ads/active; if none are live yet falls back to the
 * static channel sponsor strip (AdSlot) so the surface is never empty.
 */
const FanPanel = () => {
  const [ad, setAd] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ads/active?placement=homepage&limit=1`)
      .then((r) => setAd((r.data && r.data[0]) || null))
      .catch(() => setAd(null))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  // Live, admin-approved ad → render the real thing with a click tracker.
  if (ad) {
    // Route through /api/r/{ad_id} so the browser follows our 302 to the
    // destination. Click is logged server-side as the redirect runs —
    // attribution is guaranteed even on Safari / in-app webviews.
    const trackedHref = `${API}/r/${ad.id}`;
    return (
      <section className="bg-[#f7f6f3]" data-testid="persona-panel-fan">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Sponsored · {ad.sponsor_name}
          </p>
          <a
            href={trackedHref}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="block group"
            data-testid={`persona-fan-ad-${ad.id}`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-4 items-center bg-white rounded-2xl ring-1 ring-black/5 hover:ring-rose-400/40 transition overflow-hidden">
              {ad.image ? (
                <img src={resolveImageUrl(ad.image, 320)} alt={ad.title} loading="lazy" className="w-full h-32 sm:h-28 object-cover" />
              ) : (
                <div className="w-full h-32 sm:h-28 bg-gradient-to-br from-rose-50 to-rose-100 flex items-center justify-center">
                  <Megaphone className="w-8 h-8 text-rose-400" />
                </div>
              )}
              <div className="min-w-0 p-4 sm:pl-0">
                <h3 className="font-display text-lg sm:text-xl uppercase leading-tight mb-1 truncate group-hover:underline">{ad.title}</h3>
                {ad.description && <p className="text-sm text-gray-600 leading-snug">{ad.description}</p>}
              </div>
            </div>
          </a>
        </div>
      </section>
    );
  }

  // No live sponsor → render nothing. Fans don't need to be pitched a
  // partner slot — that surface belongs to the Brand persona, not them.
  return null;
};

/* ---------- BRAND ---------- */
const BrandPanel = () => {
  const channel = useChannel();
  const isFlyt = channel.id === 'flyt';
  // On FLYT we suppress the "Put your brand on the platform" header AND
  // the Featured Partner Slot — the 3 action cards alone read cleaner
  // against the FLYT brand chrome already at the top of the page.
  return (
    <section className="bg-gradient-to-br from-yellow-50 via-white to-yellow-100" data-testid="persona-panel-brand">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7">
        {!isFlyt && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-9 h-9 rounded-full bg-yellow-400 text-black flex items-center justify-center">
                <Megaphone className="w-4 h-4" />
              </span>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-gray-500">For brands</p>
                <h2 className="font-display text-xl sm:text-2xl uppercase leading-tight">Put your brand on the platform.</h2>
              </div>
            </div>
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ActionCard
            to="/brands#campaigns"
            icon={Sparkles}
            accent="bg-black text-yellow-300"
            eyebrow="Build a campaign"
            title="Partner with an athlete"
            body="Match with featured athletes. Build a full story-led campaign."
            cta="See packages"
            testid="brand-action-campaign"
          />
          <ActionCard
            to="/flyt/brands"
            icon={BarChart3}
            accent="bg-yellow-200 text-black"
            eyebrow="Audience data"
            title="See the numbers"
            body="4.5M+ organic views. Demographic & reach breakdowns."
            cta="View metrics"
            testid="brand-action-metrics"
          />
        </div>
      </div>
    </section>
  );
};

/* ---------- INVESTOR ---------- */
const InvestorPanel = () => (
  <section className="bg-gradient-to-br from-emerald-50 via-white to-emerald-100" data-testid="persona-panel-investor">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center">
          <FileText className="w-4 h-4" />
        </span>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-500">For investors</p>
          <h2 className="font-display text-xl sm:text-2xl uppercase leading-tight">Mission, direction, and the deck.</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ActionCard
          to="/about"
          icon={FileText}
          accent="bg-emerald-500 text-white"
          eyebrow="Mission report"
          title="The Dreams League thesis"
          body="Why this category. Why now. What we're building over the next 18 months."
          cta="Read the thesis"
          testid="investor-action-thesis"
        />
        <ActionCard
          to="/visionary/stories"
          icon={Users}
          accent="bg-black text-emerald-300"
          eyebrow="Founder stories"
          title="The Dreamers chapter"
          body="Sit-downs with the builders. Unfiltered conversations on the work behind the work."
          cta="Browse founders"
          testid="investor-action-founders"
        />
        <ActionCard
          to="/contact?type=Investor"
          icon={Download}
          accent="bg-emerald-200 text-emerald-900"
          eyebrow="Direction & deck"
          title="Request the brief"
          body="Q3 strategic roadmap, capital plan, and growth metrics on request."
          cta="Get in touch"
          testid="investor-action-deck"
        />
      </div>
    </div>
  </section>
);

/* ---------- ATHLETE ---------- */
const AthletePanel = () => (
  <section className="bg-gradient-to-br from-blue-50 via-white to-blue-100 text-black" data-testid="persona-panel-athlete">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-9 h-9 rounded-full bg-blue-500 text-white flex items-center justify-center">
          <Trophy className="w-4 h-4" />
        </span>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-600">For athletes</p>
          <h2 className="font-display text-xl sm:text-2xl uppercase leading-tight text-black">Built to put YOU on the map.</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ActionCard
          onClick={openApplyModal}
          icon={Trophy}
          accent="bg-blue-500 text-white"
          eyebrow="Get featured"
          title="Apply for a story"
          body="Pro, college, draft, rising — pitch us your chapter."
          cta="Start your application"
          testid="athlete-action-apply"
        />
        {/* iter118 · "Get matched with brands" card removed — no
            brand partnerships are live yet, so this CTA was a dead
            end. Bring it back once real brand deals are on the roster. */}
      </div>
    </div>
  </section>
);

/* ---------- SCHOOL ---------- */
const SchoolPanel = () => {
  // Pull the featured athletes the school could sponsor / collaborate with.
  const [athletes, setAthletes] = useState([]);
  useEffect(() => {
    axios.get(`${API}/athletes?featured=true&limit=4`)
      .then((r) => setAthletes(r.data || []))
      .catch(() => setAthletes([]));
  }, []);

  return (
    <section className="bg-gradient-to-br from-purple-50 via-white to-purple-100" data-testid="persona-panel-school">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-9 h-9 rounded-full bg-purple-500 text-white flex items-center justify-center">
            <GraduationCap className="w-4 h-4" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500">For schools & athletic departments</p>
            <h2 className="font-display text-xl sm:text-2xl uppercase leading-tight">The athletes you can lift.</h2>
          </div>
        </div>

        {/* Sponsorable / featured athletes row */}
        {athletes.length > 0 && (
          <div className="mb-5">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Currently featured · Join in</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {athletes.slice(0, 4).map((a) => (
                <Link
                  key={a.id}
                  to={`/network/${a.slug}`}
                  className="group block bg-white rounded-2xl ring-1 ring-black/5 p-3 hover:ring-purple-500/40 transition"
                  data-testid={`school-athlete-${a.slug}`}
                >
                  {a.photo ? (
                    <img src={resolveImageUrl(a.photo, 320)} alt={a.name} loading="lazy" className="w-full aspect-square object-cover rounded-xl mb-2" />
                  ) : (
                    <div className="w-full aspect-square rounded-xl bg-gray-100 flex items-center justify-center font-display text-3xl text-gray-400 mb-2">
                      {a.name?.[0]}
                    </div>
                  )}
                  <p className="text-[10px] uppercase tracking-wider text-purple-600 font-bold">{a.sport || 'Athlete'}</p>
                  <p className="font-display text-sm uppercase leading-tight truncate group-hover:underline">{a.name}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ActionCard
            to="/programs"
            icon={GraduationCap}
            accent="bg-purple-500 text-white"
            eyebrow="Program package"
            title="Lift your roster's story"
            body="Documentary-quality features for your athletes — single or season-series."
            cta="See packages"
            testid="school-action-package"
          />
          <ActionCard
            to="/programs"
            icon={Users}
            accent="bg-black text-purple-300"
            eyebrow="Join the network"
            title="Get your AD in the room"
            body="School-side rooms for recruiting, NIL coordination, and storytelling reps."
            cta="Start onboarding"
            testid="school-action-network"
          />
          <ActionCard
            to="/contact?type=Program"
            icon={FileText}
            accent="bg-purple-200 text-purple-900"
            eyebrow="AD resources"
            title="Athletic dept playbook"
            body="One-pager for ADs: NIL guidelines, storytelling rights, distribution."
            cta="Request the doc"
            testid="school-action-doc"
          />
        </div>
      </div>
    </section>
  );
};

/* ---------- Shared card primitive ---------- */
const ActionCard = ({ to, onClick, icon: Icon, accent, eyebrow, title, body, cta, testid }) => {
  const inner = (
    <>
      <div className="flex items-start gap-3 mb-3">
        <span className={`w-9 h-9 rounded-full ${accent} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-gray-600">{eyebrow}</p>
          <h3 className="font-display text-base uppercase leading-tight text-black">{title}</h3>
        </div>
      </div>
      <p className="text-sm text-gray-700 leading-snug mb-3">{body}</p>
      <span className="inline-flex items-center gap-1 text-sm font-bold text-black group-hover:gap-1.5 transition-all">
        {cta} <ChevronRight className="w-4 h-4" />
      </span>
    </>
  );
  const cls = "group block bg-white rounded-2xl ring-1 ring-black/5 p-5 hover:ring-black/30 hover:-translate-y-0.5 transition-all text-left w-full text-black";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>
    );
  }
  return (
    <Link to={to} className={cls} data-testid={testid}>{inner}</Link>
  );
};

export default PersonaActionPanel;
